import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Input, SearchGrid, SearchGridItem } from '@/components/ui';
import { useProductFiltersStore, type ProductSaleStatusFilter } from '@/stores/useProductFiltersStore';
import { ArtistSearchSelect } from '@/features/artist/components/ArtistSearchSelect';
import { CategorySearchSelect } from './CategorySearchSelect';

const DEBOUNCE_MS = 300;

// QA-374 — 비공개(HIDDEN = 진열 안 함)·준비중(PREPARING = 작성중·승인대기)은 상품 폼의 상품 상태와 같은 값. BE catalog 판매상태 필터 확장 필요(전달 문서).
export const SALE_STATUS_OPTIONS: ReadonlyArray<ProductSaleStatusFilter> = ['ON_SALE', 'SOLD_OUT', 'PRESALE', 'SALE_ENDED', 'HIDDEN', 'PREPARING'];
const SALE_STATUS_LABEL: Record<ProductSaleStatusFilter, string> = { ON_SALE: '판매중', SOLD_OUT: '품절', PRESALE: '예약판매', SALE_ENDED: '판매종료', HIDDEN: '비공개', PREPARING: '준비중' };

const parsePrice = (raw: string): number | null => {
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
};

type DisplayedValue = 'all' | 'shown' | 'hidden';
const toDisplayedValue = (v: boolean | null): DisplayedValue => (v === true ? 'shown' : v === false ? 'hidden' : 'all');
const fromDisplayedValue = (v: DisplayedValue): boolean | null => (v === 'shown' ? true : v === 'hidden' ? false : null);

/*
 * 상품 검색 조건. QA-348: 헤더 | 입력 2쌍씩 고정 격자(SearchGrid).
 * QA-374: 고도몰 기준으로 정리.
 *  - 검색어 하나로 상품명·상품코드·자체상품코드(BE keyword) — 상품코드·자체상품코드 칸 삭제. 유형 삭제(고도몰 대응 없음).
 *  - 판매상태 체크박스 다중선택, 진열 여부 라디오(전체/진열/미진열), 가격대 → 판매가, 수정일 추가,
 *    아티스트 연결(연결됨/일반) → 아티스트를 모달에서 골라 그 아티스트 상품 조회.
 *  - 줄 순서: 검색어·바코드 / 카테고리·아티스트 / 판매상태·진열 여부 / 판매가·재고 / 등록일·수정일.
 *  - [검색 조건 초기화]는 페이지 상단 버튼(ProductListPage)으로 옮겼다. 저장소가 비면 아래 effect 가 입력칸도 비운다.
 */
export const ProductTableToolbar = () => {
  const { t } = useTranslation('products');
  const keyword = useProductFiltersStore((s) => s.keyword);
  const barcode = useProductFiltersStore((s) => s.barcode);
  const saleStatuses = useProductFiltersStore((s) => s.saleStatuses);
  const categoryId = useProductFiltersStore((s) => s.categoryId);
  const artistId = useProductFiltersStore((s) => s.artistId);
  const priceMin = useProductFiltersStore((s) => s.priceMin);
  const priceMax = useProductFiltersStore((s) => s.priceMax);
  const stockMin = useProductFiltersStore((s) => s.stockMin);
  const stockMax = useProductFiltersStore((s) => s.stockMax);
  const createdFrom = useProductFiltersStore((s) => s.createdFrom);
  const createdTo = useProductFiltersStore((s) => s.createdTo);
  const updatedFrom = useProductFiltersStore((s) => s.updatedFrom);
  const updatedTo = useProductFiltersStore((s) => s.updatedTo);
  const displayed = useProductFiltersStore((s) => s.displayed);
  const setKeyword = useProductFiltersStore((s) => s.setKeyword);
  const setBarcode = useProductFiltersStore((s) => s.setBarcode);
  const setSaleStatuses = useProductFiltersStore((s) => s.setSaleStatuses);
  const setCategoryId = useProductFiltersStore((s) => s.setCategoryId);
  const setArtistId = useProductFiltersStore((s) => s.setArtistId);
  const setPriceMin = useProductFiltersStore((s) => s.setPriceMin);
  const setPriceMax = useProductFiltersStore((s) => s.setPriceMax);
  const setStockMin = useProductFiltersStore((s) => s.setStockMin);
  const setStockMax = useProductFiltersStore((s) => s.setStockMax);
  const setCreatedFrom = useProductFiltersStore((s) => s.setCreatedFrom);
  const setCreatedTo = useProductFiltersStore((s) => s.setCreatedTo);
  const setUpdatedFrom = useProductFiltersStore((s) => s.setUpdatedFrom);
  const setUpdatedTo = useProductFiltersStore((s) => s.setUpdatedTo);
  const setDisplayed = useProductFiltersStore((s) => s.setDisplayed);

  const [local, setLocal] = useState(keyword);
  const [localBarcode, setLocalBarcode] = useState(barcode ?? '');

  useEffect(() => {
    if (local === keyword) return;
    const timer = setTimeout(() => setKeyword(local), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [local, keyword, setKeyword]);

  useEffect(() => {
    if ((localBarcode || null) === barcode) return;
    const timer = setTimeout(() => setBarcode(localBarcode || null), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [localBarcode, barcode, setBarcode]);

  // 저장소가 다른 경로(검색 조건 초기화 등)로 비워지면 입력칸도 맞춘다.
  useEffect(() => {
    if (keyword === '') setLocal('');
  }, [keyword]);
  useEffect(() => {
    if (barcode === null) setLocalBarcode('');
  }, [barcode]);

  const toggleSaleStatus = (s: ProductSaleStatusFilter) =>
    setSaleStatuses(saleStatuses.includes(s) ? saleStatuses.filter((v) => v !== s) : [...saleStatuses, s]);

  const inputCls = 'w-full max-w-[360px]';
  const rangeCls = 'w-full max-w-[170px]';
  const tilde = (
    <span className="shrink-0 text-[12px]" style={{ color: 'var(--content-muted)' }}>
      ~
    </span>
  );
  const dateRange = (
    id: string,
    from: string | null,
    to: string | null,
    setFrom: (v: string | null) => void,
    setTo: (v: string | null) => void,
    fromLabel: string,
    toLabel: string,
  ) => (
    <div className="flex items-center gap-1.5">
      <Input
        id={`${id}-from`}
        type="date"
        value={from ?? ''}
        onChange={(e) => setFrom(e.target.value === '' ? null : e.target.value)}
        aria-label={fromLabel}
        className={rangeCls}
      />
      {tilde}
      <Input
        id={`${id}-to`}
        type="date"
        value={to ?? ''}
        onChange={(e) => setTo(e.target.value === '' ? null : e.target.value)}
        aria-label={toLabel}
        className={rangeCls}
      />
    </div>
  );

  return (
    <SearchGrid aria-label={t('products.filters.label')} testId="product-search-grid">
      <SearchGridItem label={t('products.filters.keyword')} htmlFor="product-keyword">
        <Input
          id="product-keyword"
          type="search"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder={t('products.filters.keywordPlaceholder', { defaultValue: '상품명 / 상품코드 / 자체상품코드' })}
          maxLength={100}
          className={inputCls}
        />
      </SearchGridItem>
      <SearchGridItem label={t('products.filters.barcode', { defaultValue: '바코드' })} htmlFor="product-barcode-filter">
        <Input
          id="product-barcode-filter"
          type="search"
          value={localBarcode}
          onChange={(e) => setLocalBarcode(e.target.value)}
          placeholder={t('products.filters.barcodePlaceholder', { defaultValue: '바코드 정확일치' })}
          maxLength={50}
          className={inputCls}
        />
      </SearchGridItem>

      <SearchGridItem label={t('products.filters.category')} testId="product-filter-category">
        <div className="max-w-[360px]">
          <CategorySearchSelect
            value={categoryId}
            onChange={setCategoryId}
            placeholder={t('products.filters.categoryAll')}
            testId="product-category-filter"
          />
        </div>
      </SearchGridItem>
      <SearchGridItem label={t('products.filters.artist', { defaultValue: '아티스트' })} testId="product-filter-artist">
        <div className="max-w-[360px]">
          <ArtistSearchSelect
            value={artistId}
            onChange={setArtistId}
            placeholder={t('products.filters.artistAllPlaceholder', { defaultValue: '전체 아티스트' })}
            mode="modal"
            modalTitle={t('products.filters.artistModalTitle', { defaultValue: '아티스트 선택' })}
            testId="product-artist-filter"
            ariaLabel={t('products.filters.artist', { defaultValue: '아티스트' })}
          />
        </div>
      </SearchGridItem>

      <SearchGridItem label={t('products.filters.saleStatus')}>
        <div className="flex flex-wrap items-center gap-4 text-[13px]" data-testid="product-sale-status-filter">
          {SALE_STATUS_OPTIONS.map((s) => (
            <label key={s} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <input type="checkbox" checked={saleStatuses.includes(s)} onChange={() => toggleSaleStatus(s)} />
              {t(`products.saleStatus.${s}`, SALE_STATUS_LABEL[s])}
            </label>
          ))}
        </div>
      </SearchGridItem>
      <SearchGridItem label={t('products.filters.displayed')}>
        <div className="flex flex-wrap items-center gap-4 text-[13px]" role="radiogroup" data-testid="product-displayed-filter">
          {(['all', 'shown', 'hidden'] as const).map((v) => (
            <label key={v} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <input
                type="radio"
                name="product-displayed-filter"
                checked={toDisplayedValue(displayed) === v}
                onChange={() => setDisplayed(fromDisplayedValue(v))}
              />
              {v === 'all'
                ? t('products.filters.displayedAll')
                : v === 'shown'
                  ? t('products.filters.displayedShown')
                  : t('products.filters.displayedHidden')}
            </label>
          ))}
        </div>
      </SearchGridItem>

      <SearchGridItem label={t('products.filters.priceRange')}>
        <div className="flex items-center gap-1.5">
          <Input
            id="product-price-min"
            type="number"
            inputMode="numeric"
            min={0}
            value={priceMin ?? ''}
            onChange={(e) => setPriceMin(parsePrice(e.target.value))}
            placeholder={t('products.filters.priceMin')}
            aria-label={t('products.filters.priceMin')}
            className={rangeCls}
          />
          {tilde}
          <Input
            id="product-price-max"
            type="number"
            inputMode="numeric"
            min={0}
            value={priceMax ?? ''}
            onChange={(e) => setPriceMax(parsePrice(e.target.value))}
            placeholder={t('products.filters.priceMax')}
            aria-label={t('products.filters.priceMax')}
            className={rangeCls}
          />
        </div>
      </SearchGridItem>
      <SearchGridItem label={t('products.filters.stockRange')}>
        <div className="flex items-center gap-1.5">
          <Input
            id="product-stock-min"
            type="number"
            inputMode="numeric"
            min={0}
            value={stockMin ?? ''}
            onChange={(e) => setStockMin(parsePrice(e.target.value))}
            placeholder={t('products.filters.stockMin')}
            aria-label={t('products.filters.stockMin')}
            className={rangeCls}
          />
          {tilde}
          <Input
            id="product-stock-max"
            type="number"
            inputMode="numeric"
            min={0}
            value={stockMax ?? ''}
            onChange={(e) => setStockMax(parsePrice(e.target.value))}
            placeholder={t('products.filters.stockMax')}
            aria-label={t('products.filters.stockMax')}
            className={rangeCls}
          />
        </div>
      </SearchGridItem>

      <SearchGridItem label={t('products.filters.createdRange', { defaultValue: '등록일' })}>
        {dateRange(
          'product-created',
          createdFrom,
          createdTo,
          setCreatedFrom,
          setCreatedTo,
          t('products.filters.createdFrom'),
          t('products.filters.createdTo'),
        )}
      </SearchGridItem>
      <SearchGridItem label={t('products.filters.updatedRange', { defaultValue: '수정일' })}>
        {dateRange(
          'product-updated',
          updatedFrom,
          updatedTo,
          setUpdatedFrom,
          setUpdatedTo,
          t('products.filters.updatedFrom', { defaultValue: '수정일 시작' }),
          t('products.filters.updatedTo', { defaultValue: '수정일 종료' }),
        )}
      </SearchGridItem>
    </SearchGrid>
  );
};
